import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import MarketingPageLayout from '../components/marketing/MarketingPageLayout';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export default function PrivacyPolicy() {
  return (
    <MarketingPageLayout>
      {/* Header */}
      <section className="pt-24 pb-12 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-slate-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-slate-400">
              Last Updated: December 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="prose prose-invert prose-slate max-w-none"
          >
            <div className="text-slate-300 space-y-8">
              <p>
                Clear Defense AI Inc. (&quot;Clear Defense AI&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you use our cybersecurity platform, software, agents, dashboards, APIs, websites, and related services (collectively, the &quot;Services&quot;).
              </p>
              
              <p>
                By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with this Privacy Policy, please do not access or use our Services.
              </p>

              {/* Section 1 */}
              <div className="border-t border-slate-800 pt-8">
                <h2 className="text-xl font-semibold text-white mb-4">1. Information We Collect</h2>
                
                <p><strong className="text-white">1.1 Information You Provide to Us</strong></p>
                <p>We collect information you provide directly to us, including:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Account Information:</strong> Name, email address, company name, job title, phone number, and password when you create an account.</li>
                  <li><strong>Billing Information:</strong> Payment card details, billing address, and transaction history (processed by our third-party payment processors).</li>
                  <li><strong>Communication Data:</strong> Information you provide when you contact us for support, submit feedback, or participate in surveys.</li>
                  <li><strong>Configuration Data:</strong> Security policies, rules, settings, and preferences you configure within the Services.</li>
                </ul>
                
                <p><strong className="text-white">1.2 Information We Collect Automatically</strong></p>
                <p>When you use our Services, we automatically collect certain information, including:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers, browser type, and version.</li>
                  <li><strong>Usage Data:</strong> Features used, actions taken, timestamps, frequency and duration of activities.</li>
                  <li><strong>Log Data:</strong> IP addresses, access times, pages viewed, links clicked, and referring URLs.</li>
                  <li><strong>Security Telemetry:</strong> DNS queries, file hashes, application behavior, email metadata, and threat detection data collected by our Agents.</li>
                </ul>
                
                <p><strong className="text-white">1.3 Information from Third Parties</strong></p>
                <p>We may receive information about you from third parties, including:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Identity verification services</li>
                  <li>Threat intelligence feeds and security research databases</li>
                  <li>Business partners and resellers</li>
                  <li>Publicly available sources</li>
                </ul>
              </div>

              {/* Section 2 */}
              <div className="border-t border-slate-800 pt-8">
                <h2 className="text-xl font-semibold text-white mb-4">2. How We Use Your Information</h2>
                
                <p>We use the information we collect for the following purposes:</p>
                
                <p><strong className="text-white">2.1 Service Delivery</strong></p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Provide, operate, maintain, and improve our Services</li>
                  <li>Process transactions and send related information</li>
                  <li>Detect, prevent, and respond to security threats and incidents</li>
                  <li>Provide customer support and respond to your requests</li>
                </ul>
                
                <p><strong className="text-white">2.2 Security and Threat Analysis</strong></p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Analyze security telemetry to identify and block threats</li>
                  <li>Improve our threat detection algorithms and models</li>
                  <li>Develop new security features and capabilities</li>
                  <li>Conduct security research using aggregated, anonymized data</li>
                </ul>
                
                <p><strong className="text-white">2.3 Communications</strong></p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Send security alerts, updates, and administrative messages</li>
                  <li>Provide information about products, services, and events</li>
                  <li>Respond to your comments, questions, and support requests</li>
                </ul>
                
                <p><strong className="text-white">2.4 Legal and Compliance</strong></p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Comply with applicable laws, regulations, and legal processes</li>
                  <li>Enforce our Terms of Service and other agreements</li>
                  <li>Protect our rights, privacy, safety, or property</li>
                </ul>
              </div>

              {/* Section 3 */}
              <div className="border-t border-slate-800 pt-8">
                <h2 className="text-xl font-semibold text-white mb-4">3. How We Share Your Information</h2>
                
                <p>We may share your information in the following circumstances:</p>
                
                <p><strong className="text-white">3.1 Service Providers</strong></p>
                <p>We share information with third-party service providers who perform services on our behalf, including:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Cloud hosting and infrastructure providers</li>
                  <li>Payment processors and billing services</li>
                  <li>Customer support and communication tools</li>
                  <li>Analytics and monitoring services</li>
                </ul>
                
                <p><strong className="text-white">3.2 Business Transfers</strong></p>
                <p>If Clear Defense AI is involved in a merger, acquisition, reorganization, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change in ownership or control.</p>
                
                <p><strong className="text-white">3.3 Legal Requirements</strong></p>
                <p>We may disclose information if required by law, regulation, legal process, or governmental request, or when we believe disclosure is necessary to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Comply with applicable laws or legal processes</li>
                  <li>Enforce our Terms of Service</li>
                  <li>Protect the rights, property, or safety of Clear Defense AI, our customers, or others</li>
                  <li>Detect, prevent, or address fraud, security, or technical issues</li>
                </ul>
                
                <p><strong className="text-white">3.4 With Your Consent</strong></p>
                <p>We may share your information with third parties when you have given us explicit consent to do so.</p>
                
                <p><strong className="text-white">3.5 Aggregated and De-identified Data</strong></p>
                <p>We may share aggregated, anonymized, or de-identified information that cannot reasonably be used to identify you for research, analytics, and industry reporting purposes.</p>
              </div>

              {/* Section 4 */}
              <div className="border-t border-slate-800 pt-8">
                <h2 className="text-xl font-semibold text-white mb-4">4. Data Security</h2>
                
                <p>We implement industry-standard technical and organizational measures to protect your information, including:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Encryption:</strong> Data encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
                  <li><strong>Access Controls:</strong> Role-based access controls and multi-factor authentication</li>
                  <li><strong>Monitoring:</strong> Continuous security monitoring and logging</li>
                  <li><strong>Incident Response:</strong> Documented incident response procedures</li>
                  <li><strong>Employee Training:</strong> Regular security awareness training for employees</li>
                  <li><strong>Vendor Management:</strong> Security assessments of third-party vendors</li>
                </ul>
                
                <p>While we strive to protect your information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.</p>
              </div>

              {/* Section 5 */}
              <div className="border-t border-slate-800 pt-8">
                <h2 className="text-xl font-semibold text-white mb-4">5. Data Retention</h2>
                
                <p>We retain your information for as long as necessary to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Provide our Services to you</li>
                  <li>Comply with legal obligations</li>
                  <li>Resolve disputes and enforce our agreements</li>
                  <li>Conduct security research and improve our Services</li>
                </ul>
                
                <p>When information is no longer needed, we will securely delete or anonymize it. Security telemetry data is typically retained for 90 days, while account information is retained for the duration of your subscription plus 30 days for data export purposes.</p>
              </div>

              {/* Section 6 */}
              <div className="border-t border-slate-800 pt-8">
                <h2 className="text-xl font-semibold text-white mb-4">6. Your Rights and Choices</h2>
                
                <p>Depending on your location and applicable law, you may have the following rights:</p>
                
                <p><strong className="text-white">6.1 Access and Portability</strong></p>
                <p>You may request access to your personal information and receive a copy in a structured, commonly used format.</p>
                
                <p><strong className="text-white">6.2 Correction</strong></p>
                <p>You may request correction of inaccurate or incomplete personal information.</p>
                
                <p><strong className="text-white">6.3 Deletion</strong></p>
                <p>You may request deletion of your personal information, subject to certain exceptions (e.g., legal retention requirements).</p>
                
                <p><strong className="text-white">6.4 Restriction and Objection</strong></p>
                <p>You may request restriction of processing or object to processing of your personal information in certain circumstances.</p>
                
                <p><strong className="text-white">6.5 Withdrawal of Consent</strong></p>
                <p>Where processing is based on consent, you may withdraw your consent at any time without affecting the lawfulness of prior processing.</p>
                
                <p><strong className="text-white">6.6 Marketing Communications</strong></p>
                <p>You may opt out of marketing communications at any time by clicking the &quot;unsubscribe&quot; link in our emails or contacting us directly. Note that you will continue to receive transactional and service-related communications.</p>
                
                <p>To exercise any of these rights, please contact us at privacy@cleardefenseai.com.</p>
              </div>

              {/* Section 7 */}
              <div className="border-t border-slate-800 pt-8">
                <h2 className="text-xl font-semibold text-white mb-4">7. International Data Transfers</h2>
                
                <p>Clear Defense AI is headquartered in Toronto, Ontario, Canada. Your information may be transferred to, stored, and processed in Canada or other countries where we or our service providers operate.</p>
                
                <p>When we transfer personal information across borders, we ensure appropriate safeguards are in place, including:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Standard contractual clauses approved by relevant authorities</li>
                  <li>Compliance with applicable data protection frameworks</li>
                  <li>Adequacy decisions where applicable</li>
                </ul>
              </div>

              {/* Section 8 */}
              <div className="border-t border-slate-800 pt-8">
                <h2 className="text-xl font-semibold text-white mb-4">8. Cookies and Tracking Technologies</h2>
                
                <p>We use cookies and similar tracking technologies to collect information about your interactions with our Services. These technologies help us:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Remember your preferences and settings</li>
                  <li>Authenticate users and prevent fraud</li>
                  <li>Analyze usage patterns and improve our Services</li>
                  <li>Deliver relevant content and communications</li>
                </ul>
                
                <p><strong className="text-white">Types of Cookies We Use:</strong></p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Essential Cookies:</strong> Required for the Services to function properly</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our Services</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                </ul>
                
                <p>You can control cookies through your browser settings. Note that disabling certain cookies may affect the functionality of our Services.</p>
              </div>

              {/* Section 9 */}
              <div className="border-t border-slate-800 pt-8">
                <h2 className="text-xl font-semibold text-white mb-4">9. Children&apos;s Privacy</h2>
                
                <p>Our Services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child without parental consent, we will take steps to delete that information.</p>
              </div>

              {/* Section 10 */}
              <div className="border-t border-slate-800 pt-8">
                <h2 className="text-xl font-semibold text-white mb-4">10. Third-Party Links and Services</h2>
                
                <p>Our Services may contain links to third-party websites, applications, or services. This Privacy Policy does not apply to those third parties. We encourage you to review the privacy policies of any third-party services you access.</p>
              </div>

              {/* Section 11 */}
              <div className="border-t border-slate-800 pt-8">
                <h2 className="text-xl font-semibold text-white mb-4">11. Canadian Privacy Law Compliance</h2>
                
                <p>Clear Defense AI complies with the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy legislation. Under Canadian law, you have the right to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Know what personal information we hold about you</li>
                  <li>Challenge the accuracy and completeness of your information</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Withdraw consent to collection, use, or disclosure (subject to legal or contractual restrictions)</li>
                </ul>
                
                <p>If you have concerns about our privacy practices, you may contact the Office of the Privacy Commissioner of Canada.</p>
              </div>

              {/* Section 12 */}
              <div className="border-t border-slate-800 pt-8">
                <h2 className="text-xl font-semibold text-white mb-4">12. Changes to This Privacy Policy</h2>
                
                <p>We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. If we make material changes, we will notify you by:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Posting the updated Privacy Policy on our website</li>
                  <li>Sending you an email notification</li>
                  <li>Displaying a prominent notice within our Services</li>
                </ul>
                
                <p>Your continued use of our Services after any changes indicates your acceptance of the updated Privacy Policy.</p>
              </div>

              {/* Section 13 */}
              <div className="border-t border-slate-800 pt-8">
                <h2 className="text-xl font-semibold text-white mb-4">13. Contact Us</h2>
                
                <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us at:</p>
                
                <div className="bg-slate-800/50 p-6 rounded-lg mt-4">
                  <p className="text-white font-medium">Clear Defense AI Inc.</p>
                  <p>Privacy Officer</p>
                  <p>Toronto, Ontario, Canada</p>
                  <p className="mt-2">Email: privacy@cleardefenseai.com</p>
                </div>
                
                <p className="mt-4">We will respond to your inquiry within a reasonable timeframe, typically within 30 days.</p>
              </div>

            </div>
          </motion.div>
        </div>
      </section>
    </MarketingPageLayout>
  );
}
